import React from 'react';
import { Plus, Trash } from '@phosphor-icons/react';

// Value: array of { reminder_id?, offset_minutes: number, channels: string[] }
// onChange(nextArray)

const OFFSETS = [
  { m: 5,     el: '5 λεπτά',   en: '5 minutes' },
  { m: 15,    el: '15 λεπτά',  en: '15 minutes' },
  { m: 30,    el: '30 λεπτά',  en: '30 minutes' },
  { m: 60,    el: '1 ώρα',     en: '1 hour' },
  { m: 120,   el: '2 ώρες',    en: '2 hours' },
  { m: 1440,  el: '1 ημέρα',   en: '1 day' },
  { m: 2880,  el: '2 ημέρες',  en: '2 days' },
  { m: 10080, el: '1 εβδομάδα', en: '1 week' },
];

const CHANNELS = ['in_app', 'email'];
const CHAN_LABEL = { in_app: { el: 'Εντός εφαρμογής', en: 'In-app' }, email: { el: 'Email', en: 'Email' } };

export default function ReminderEditor({ value, onChange, language = 'el' }) {
  const items = value || [];
  const [newOffset, setNewOffset] = React.useState(60);
  const [newChannels, setNewChannels] = React.useState(new Set(['in_app']));

  const add = () => {
    if (newChannels.size === 0) return;
    const rid = `rem_tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    onChange([...(items || []), {
      reminder_id: rid,
      offset_minutes: newOffset,
      channels: Array.from(newChannels),
    }]);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  const toggleChannel = (c) => setNewChannels(prev => {
    const n = new Set(prev);
    if (n.has(c)) n.delete(c); else n.add(c);
    return n;
  });

  const label = (offset) => (OFFSETS.find(o => o.m === offset) || {})[language] || `${offset} min`;

  return (
    <div className="border border-zinc-200 p-3 space-y-2" data-testid="reminder-editor">
      <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500">
        {language === 'el' ? 'Υπενθυμίσεις' : 'Reminders'}
      </div>

      {items.length === 0 && (
        <div className="text-xs text-zinc-400 italic">
          {language === 'el' ? 'Δεν έχουν οριστεί υπενθυμίσεις' : 'No reminders set'}
        </div>
      )}

      {items.map((r, idx) => (
        <div key={r.reminder_id || idx}
             className="flex items-center gap-2 text-sm bg-zinc-50 border border-zinc-200 px-2 py-1"
             data-testid={`reminder-item-${idx}`}>
          <span className="flex-1">
            {label(r.offset_minutes)} {language === 'el' ? 'πριν' : 'before'}
            <span className="text-zinc-400"> · </span>
            <span className="text-xs text-zinc-500">
              {(r.channels || []).map(c => CHAN_LABEL[c]?.[language] || c).join(' + ')}
            </span>
          </span>
          <button
            type="button"
            onClick={() => remove(idx)}
            className="p-1 text-red-500 hover:text-red-700"
            data-testid={`reminder-remove-${idx}`}
          ><Trash size={14} /></button>
        </div>
      ))}

      <div className="pt-2 border-t border-zinc-100 flex items-center gap-2 flex-wrap">
        <select
          value={newOffset}
          onChange={(e) => setNewOffset(parseInt(e.target.value, 10))}
          className="border border-zinc-300 px-2 py-1 text-sm rounded-none bg-white"
          data-testid="reminder-offset-select"
        >
          {OFFSETS.map(o => (
            <option key={o.m} value={o.m}>{o[language]}</option>
          ))}
        </select>
        <span className="text-xs text-zinc-500">{language === 'el' ? 'πριν' : 'before'}</span>
        {CHANNELS.map(c => (
          <label key={c} className="flex items-center gap-1 text-xs">
            <input
              type="checkbox"
              checked={newChannels.has(c)}
              onChange={() => toggleChannel(c)}
              data-testid={`reminder-channel-${c}`}
            />
            {CHAN_LABEL[c][language]}
          </label>
        ))}
        <button
          type="button"
          onClick={add}
          disabled={newChannels.size === 0}
          className="inline-flex items-center gap-1 border border-zinc-300 hover:bg-zinc-50 px-2 py-1 text-xs disabled:opacity-40"
          data-testid="reminder-add-btn"
        ><Plus size={12} /> {language === 'el' ? 'Προσθήκη' : 'Add'}</button>
      </div>
    </div>
  );
}
